/**
 * Admin Users Page
 *
 * Comprehensive user management dashboard for admins.
 * Features: tabbed views, search/filter, user detail audit drawer,
 * RBAC controls (role change, suspend, delete, reset password).
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserPlus,
  Shield,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle,
  X,
  Loader2,
  Users,
  User,
  RefreshCw,
  Trash2,
  Ban,
  Unlock,
  Key,
  MoreVertical,
  Search,
  ChevronRight,
  Clock,
  ShieldCheck,
  Building2,
  Palette,
  Eye,
  ArrowUpDown,
  UserCog,
  CalendarDays,
  FileText,
  Copy,
  HandCoins,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api, adminUsersApi } from '../lib/api';
import type { AdminUserDetail, AdminUsersResponse } from '../lib/api';
import clsx from 'clsx';
import { useAuthStore } from '../stores/auth';

type TabFilter = 'all' | 'admins' | 'users' | 'clients';
type StatusFilter = 'all' | 'active' | 'suspended' | 'pending';
type SortField = 'email' | 'role' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
const GOD_MODE_ADMIN_EMAIL = 'admin@tyaus.com';

export default function AdminUsersPage() {
  const user = useAuthStore((s) => s.user);
  const isGodModeAdmin = (user?.email ?? '').toLowerCase() === GOD_MODE_ADMIN_EMAIL;

  // Data state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [usersData, setUsersData] = useState<AdminUsersResponse>({ admins: [], users: [], clients: [] });

  // Filter/search state
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Action menus and modals
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState<string | null>(null);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState<string | null>(null);
  const [showGodCreditModal, setShowGodCreditModal] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);

  // Form state
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [suspending, setSuspending] = useState<string | null>(null);
  const [changingRole, setChangingRole] = useState(false);
  const [godAmount, setGodAmount] = useState('');
  const [godNote, setGodNote] = useState('');
  const [submittingGodCredit, setSubmittingGodCredit] = useState(false);

  // Map client dashboard users to unified AdminUserDetail format
  const mappedClientUsers: AdminUserDetail[] = useMemo(() => {
    return (usersData.clients || []).map((c) => ({
      id: c.id,
      email: c.email,
      role: 'client' as const,
      displayName: c.displayName,
      company: null,
      jobTitle: null,
      phone: null,
      bio: null,
      avatarUrl: null,
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      twoFactorEnabled: c.twoFactorEnabled,
      emailVerified: c.emailVerified,
      isActivated: c.isActivated,
      suspended: c.suspended,
      theme: 'dark' as const,
      compactMode: false,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      source: 'client-dashboard' as const,
      subscription: c.subscription,
      lastLoginAt: c.lastLoginAt,
    }));
  }, [usersData]);

  // All users combined (admins + staff + clients)
  const allUsers = useMemo(() => {
    return [...usersData.admins, ...usersData.users, ...mappedClientUsers];
  }, [usersData, mappedClientUsers]);

  // Filtered and sorted users (works for all tabs)
  const filteredUsers = useMemo(() => {
    let users = [...allUsers];

    // Tab filter
    if (activeTab === 'admins') {
      users = users.filter((u) => u.role === 'admin');
    } else if (activeTab === 'users') {
      users = users.filter((u) => u.role === 'user');
    } else if (activeTab === 'clients') {
      users = users.filter((u) => u.source === 'client-dashboard');
    }

    // Status filter
    if (statusFilter === 'active') {
      users = users.filter((u) => u.isActivated && !u.suspended);
    } else if (statusFilter === 'suspended') {
      users = users.filter((u) => u.suspended);
    } else if (statusFilter === 'pending') {
      users = users.filter((u) => !u.isActivated);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      users = users.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.displayName && u.displayName.toLowerCase().includes(q)) ||
          (u.company && u.company.toLowerCase().includes(q)) ||
          (u.jobTitle && u.jobTitle.toLowerCase().includes(q)) ||
          u.id.toLowerCase().includes(q),
      );
    }

    // Sort
    users.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'email') {
        cmp = a.email.localeCompare(b.email);
      } else if (sortField === 'role') {
        cmp = a.role.localeCompare(b.role);
      } else if (sortField === 'createdAt') {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortField === 'updatedAt') {
        cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      }
      return sortDirection === 'desc' ? -cmp : cmp;
    });

    return users;
  }, [allUsers, activeTab, statusFilter, searchQuery, sortField, sortDirection]);

  // Stats
  const stats = useMemo(() => {
    const total = allUsers.length;
    const admins = allUsers.filter((u) => u.role === 'admin').length;
    const internalUsers = allUsers.filter((u) => u.role === 'user').length;
    const clients = allUsers.filter((u) => u.source === 'client-dashboard').length;
    const active = allUsers.filter((u) => u.isActivated && !u.suspended).length;
    const suspended = allUsers.filter((u) => u.suspended).length;
    const pending = allUsers.filter((u) => !u.isActivated).length;
    const twoFaEnabled = allUsers.filter((u) => u.twoFactorEnabled).length;
    return { total, admins, internalUsers, clients, active, suspended, pending, twoFaEnabled };
  }, [allUsers]);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const data = await adminUsersApi.getAllUsers();
      setUsersData(data);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to fetch users');
      } else {
        toast.error('Failed to fetch users. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Close action menu when clicking outside
  useEffect(() => {
    if (!actionMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-action-menu]') && !target.closest('button[data-action-button]')) {
        setActionMenuOpen(null);
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ──────────────────────────────────────────────
  // Action handlers
  // ──────────────────────────────────────────────

  const validateCreateForm = (): string | null => {
    if (!email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    if (!email.toLowerCase().endsWith('@tyaus.com'))
      return 'Admin accounts can only be created with @tyaus.com email addresses';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleCreateAdmin = async () => {
    const error = validateCreateForm();
    if (error) {
      toast.error(error);
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/v1/auth/create-admin', { email: email.trim(), password });
      toast.success('Admin account created successfully. Activation email sent.');
      setShowCreateModal(false);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to create admin account');
      } else {
        toast.error('Failed to create admin account. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    setDeleting(true);
    try {
      await adminUsersApi.deleteUser(userId);
      toast.success(`User ${userEmail} deleted successfully`);
      setShowDeleteModal(null);
      if (selectedUser?.id === userId) setSelectedUser(null);
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to delete user');
      } else {
        toast.error('Failed to delete user. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSuspendUser = async (userId: string, userEmail: string) => {
    setSuspending(userId);
    try {
      await adminUsersApi.suspendUser(userId);
      toast.success(`User ${userEmail} suspended successfully`);
      setActionMenuOpen(null);
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to suspend user');
      } else {
        toast.error('Failed to suspend user. Please try again.');
      }
    } finally {
      setSuspending(null);
    }
  };

  const handleUnsuspendUser = async (userId: string, userEmail: string) => {
    setSuspending(userId);
    try {
      await adminUsersApi.unsuspendUser(userId);
      toast.success(`User ${userEmail} unsuspended successfully`);
      setActionMenuOpen(null);
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to unsuspend user');
      } else {
        toast.error('Failed to unsuspend user. Please try again.');
      }
    } finally {
      setSuspending(null);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!newPassword.trim()) {
      toast.error('Password is required');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setResettingPassword(true);
    try {
      await adminUsersApi.resetPassword(userId, newPassword);
      toast.success('Password reset successfully');
      setShowResetPasswordModal(null);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to reset password');
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'admin' | 'user') => {
    setChangingRole(true);
    try {
      await adminUsersApi.changeRole(userId, newRole);
      toast.success(`Role changed to ${newRole} successfully`);
      setShowRoleChangeModal(null);
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, role: newRole });
      }
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to change role');
      } else {
        toast.error('Failed to change role. Please try again.');
      }
    } finally {
      setChangingRole(false);
    }
  };

  const openGodCreditModal = (targetUserId: string) => {
    setShowGodCreditModal(targetUserId);
    setGodAmount('');
    setGodNote('');
  };

  const closeGodCreditModal = () => {
    setShowGodCreditModal(null);
    setGodAmount('');
    setGodNote('');
  };

  const handleSubmitGodCredit = async () => {
    if (!showGodCreditModal) return;

    const amount = Number(godAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid amount greater than 0');
      return;
    }

    const target = allUsers.find((u) => u.id === showGodCreditModal);
    if (!target || target.source !== 'client-dashboard') {
      toast.error('God mode credit is only available for client dashboard users');
      return;
    }

    setSubmittingGodCredit(true);
    try {
      await api.post('/api/v1/admin/finance/god-credit', {
        targetUserId: showGodCreditModal,
        amountUsd: amount,
        note: godNote.trim() || undefined,
      });
      toast.success(`Credited $${amount.toFixed(2)} to ${target.email}`);
      closeGodCreditModal();
      fetchUsers();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        toast.error(axiosError.response?.data?.message || 'Failed to credit client balance');
      } else {
        toast.error('Failed to credit client balance. Please try again.');
      }
    } finally {
      setSubmittingGodCredit(false);
    }
  };

  // Helpers
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatRelativeDate = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getUserEmail = (userId: string): string => {
    const user = allUsers.find((u) => u.id === userId);
    return user?.email || 'Unknown';
  };


  const getStatusBadge = (user: AdminUserDetail) => {
    if (user.suspended)
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
          Suspended
        </span>
      );
    if (!user.isActivated)
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          Pending Activation
        </span>
      );
    return (
      <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
        Active
      </span>
    );
  };

  const getRoleBadge = (role: string, source?: string) => {
    if (role === 'admin')
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-gpu-500/20 text-gpu-400 border border-gpu-500/30 flex items-center gap-1">
          <Shield className="w-3 h-3" /> Admin
        </span>
      );
    if (source === 'client-dashboard' || role === 'client')
      return (
        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1">
          <User className="w-3 h-3" /> Client
        </span>
      );
    return (
      <span className="px-2 py-0.5 text-xs rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30 flex items-center gap-1">
        <User className="w-3 h-3" /> Staff
      </span>
    );
  };

  const getInitials = (email: string, displayName?: string | null) => {
    if (displayName) {
      const parts = displayName.split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return displayName.substring(0, 2).toUpperCase();
    }
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return email.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (email: string) => {
    const colors = [
      'from-blue-500 to-indigo-600',
      'from-emerald-500 to-teal-600',
      'from-violet-500 to-purple-600',
      'from-rose-500 to-pink-600',
      'from-amber-500 to-orange-600',
      'from-cyan-500 to-blue-600',
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // ──────────────────────────────────────────────
  // Loading state
  // ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card p-8 text-center">
          <Loader2 className="w-8 h-8 text-gpu-400 mx-auto mb-4 animate-spin" />
          <p className="text-surface-400">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-gpu-500/20 to-teal-500/20 flex items-center justify-center border border-gpu-500/30 flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-gpu-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-surface-100">User Management</h1>
              <p className="text-sm sm:text-base text-surface-400 mt-1">
                Manage, audit, and control access for all registered accounts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleRefresh} disabled={refreshing} className="btn-secondary flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Create Admin</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, icon: Users, color: 'text-surface-300', bg: 'bg-surface-800/50' },
          { label: 'Admins', value: stats.admins, icon: Shield, color: 'text-gpu-400', bg: 'bg-gpu-500/10' },
          { label: 'Clients', value: stats.clients, icon: User, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Active', value: stats.active, icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Suspended', value: stats.suspended, icon: Ban, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { label: '2FA On', value: stats.twoFaEnabled, icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        ].map((stat) => (
          <div key={stat.label} className="card !p-3 flex items-center gap-3">
            <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', stat.bg)}>
              <stat.icon className={clsx('w-4 h-4', stat.color)} />
            </div>
            <div>
              <p className="text-lg font-bold text-surface-100">{stat.value}</p>
              <p className="text-xs text-surface-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Tabs + Search + Filters */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-surface-800/50 rounded-lg p-1 overflow-x-auto">
            {([
              { key: 'all' as TabFilter, label: 'All Users', count: stats.total },
              { key: 'admins' as TabFilter, label: 'Admins', count: stats.admins },
              { key: 'users' as TabFilter, label: 'Staff', count: stats.internalUsers },
              { key: 'clients' as TabFilter, label: 'Clients', count: stats.clients },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                  activeTab === tab.key
                    ? 'bg-gpu-500/20 text-gpu-400 shadow-sm'
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800',
                )}
              >
                {tab.label}
                <span
                  className={clsx(
                    'ml-2 px-1.5 py-0.5 text-xs rounded-full',
                    activeTab === tab.key ? 'bg-gpu-500/30 text-gpu-300' : 'bg-surface-700 text-surface-400',
                  )}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="input !w-auto text-sm !py-2"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="pending">Pending</option>
            </select>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
              <input
                type="text"
                placeholder="Search by name, email, company..."
                className="input !pl-10 text-sm !py-2 w-full sm:w-72"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-surface-500 hover:text-surface-300" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Users Table + Detail Drawer layout */}
      <div className="flex gap-6">
        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={clsx('card flex-1 overflow-hidden', selectedUser ? 'lg:max-w-[calc(100%-420px)]' : '')}
        >
          <div className="overflow-x-auto">
          {/* Table Header */}
          <div className="grid grid-cols-[minmax(0,2fr)_100px_110px_130px_130px_48px] gap-3 px-4 py-3 border-b border-surface-800/50 text-xs font-semibold text-surface-500 uppercase tracking-wider min-w-[700px]">
            <button onClick={() => handleSort('email')} className="flex items-center gap-1 hover:text-surface-300 text-left">
              User {sortField === 'email' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <button onClick={() => handleSort('role')} className="flex items-center gap-1 hover:text-surface-300">
              Role {sortField === 'role' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <span>Status</span>
            <button onClick={() => handleSort('createdAt')} className="flex items-center gap-1 hover:text-surface-300">
              Created {sortField === 'createdAt' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <button onClick={() => handleSort('updatedAt')} className="flex items-center gap-1 hover:text-surface-300">
              Updated {sortField === 'updatedAt' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <span></span>
          </div>

          {/* Table Body */}
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No users found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800/30">
              {filteredUsers.map((u) => (
                <div
                  key={u.source === 'client-dashboard' ? `client-${u.id}` : u.id}
                  onClick={() => setSelectedUser(u)}
                  className={clsx(
                    'grid grid-cols-[minmax(0,2fr)_100px_110px_130px_130px_48px] gap-3 px-4 py-3 hover:bg-surface-800/30 transition-colors cursor-pointer items-center min-w-[700px]',
                    selectedUser?.id === u.id && 'bg-surface-800/40 border-l-2 border-l-gpu-500',
                  )}
                >
                  {/* User Info */}
                  <div className="flex items-center gap-3 min-w-0">
                    {u.avatarUrl ? (
                      <img src={u.avatarUrl} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div
                        className={clsx(
                          'w-9 h-9 rounded-lg bg-gradient-to-br flex items-center justify-center text-white text-xs font-semibold flex-shrink-0',
                          getAvatarColor(u.email),
                        )}
                      >
                        {getInitials(u.email, u.displayName)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-surface-100 truncate">
                        {u.displayName || u.email.split('@')[0]}
                      </p>
                      <p className="text-xs text-surface-500 truncate">{u.email}</p>
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    {getRoleBadge(u.role, u.source)}
                    {u.subscription && (
                      <span className="block text-[10px] text-surface-500 mt-0.5">{u.subscription.plan}</span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-1">
                    {getStatusBadge(u)}
                    {u.twoFactorEnabled && (
                      <span title="2FA enabled" className="text-emerald-400">
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>

                  {/* Created */}
                  <span className="text-xs text-surface-400">{formatRelativeDate(u.createdAt)}</span>

                  {/* Updated */}
                  <span className="text-xs text-surface-400">{formatRelativeDate(u.updatedAt)}</span>

                  {/* Action Menu */}
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-action-button
                      onClick={() => setActionMenuOpen(actionMenuOpen === u.id ? null : u.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-surface-400 hover:bg-surface-800 hover:text-surface-200 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {actionMenuOpen === u.id && (
                      <div data-action-menu className="absolute right-0 top-10 z-20 w-52 rounded-lg bg-surface-800 border border-surface-700 shadow-xl">
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-surface-200 hover:bg-surface-700 flex items-center gap-2 rounded-t-lg"
                        >
                          <Eye className="w-4 h-4" /> View Details
                        </button>
                        <button
                          onClick={() => {
                            setShowResetPasswordModal(u.id);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-surface-200 hover:bg-surface-700 flex items-center gap-2"
                        >
                          <Key className="w-4 h-4" /> Reset Password
                        </button>
                        {u.source !== 'client-dashboard' && (
                          <button
                            onClick={() => {
                              setShowRoleChangeModal(u.id);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-surface-200 hover:bg-surface-700 flex items-center gap-2"
                          >
                            <UserCog className="w-4 h-4" /> Change Role
                          </button>
                        )}
                        {isGodModeAdmin && u.source === 'client-dashboard' && (
                          <button
                            onClick={() => {
                              openGodCreditModal(u.id);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-emerald-300 hover:bg-surface-700 flex items-center gap-2"
                          >
                            <HandCoins className="w-4 h-4" /> God Credit Balance
                          </button>
                        )}
                        {u.suspended ? (
                          <button
                            onClick={() => handleUnsuspendUser(u.id, u.email)}
                            disabled={suspending === u.id}
                            className="w-full px-4 py-2.5 text-left text-sm text-surface-200 hover:bg-surface-700 flex items-center gap-2"
                          >
                            <Unlock className="w-4 h-4" />
                            {suspending === u.id ? 'Unsuspending...' : 'Unsuspend'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspendUser(u.id, u.email)}
                            disabled={suspending === u.id}
                            className="w-full px-4 py-2.5 text-left text-sm text-surface-200 hover:bg-surface-700 flex items-center gap-2"
                          >
                            <Ban className="w-4 h-4" />
                            {suspending === u.id ? 'Suspending...' : 'Suspend'}
                          </button>
                        )}
                        <div className="border-t border-surface-700 my-1" />
                        <button
                          onClick={() => {
                            setShowDeleteModal(u.id);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-surface-700 flex items-center gap-2 rounded-b-lg"
                        >
                          <Trash2 className="w-4 h-4" /> Delete Account
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          </div>
          {/* Table Footer */}
          <div className="px-4 py-3 border-t border-surface-800/50 text-xs text-surface-500">
            Showing {filteredUsers.length} of {allUsers.length} users
          </div>
        </motion.div>

        {/* Detail Drawer (right side) */}
        <AnimatePresence>
          {selectedUser && (
            <motion.div
              initial={{ opacity: 0, x: 20, width: 0 }}
              animate={{ opacity: 1, x: 0, width: 400 }}
              exit={{ opacity: 0, x: 20, width: 0 }}
              transition={{ duration: 0.2 }}
              className="hidden lg:block flex-shrink-0"
            >
              <div className="card sticky top-0 overflow-y-auto max-h-[calc(100vh-140px)]">
                {/* Drawer Header */}
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-surface-100">Account Audit</h3>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-surface-400 hover:bg-surface-800 hover:text-surface-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Profile Section */}
                <div className="flex items-center gap-3 mb-5">
                  {selectedUser.avatarUrl ? (
                    <img src={selectedUser.avatarUrl} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <div
                      className={clsx(
                        'w-14 h-14 rounded-xl bg-gradient-to-br flex items-center justify-center text-white font-semibold text-lg',
                        getAvatarColor(selectedUser.email),
                      )}
                    >
                      {getInitials(selectedUser.email, selectedUser.displayName)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-surface-100 truncate">
                      {selectedUser.displayName || selectedUser.email.split('@')[0]}
                    </p>
                    <p className="text-sm text-surface-400 truncate">{selectedUser.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getRoleBadge(selectedUser.role, selectedUser.source)}
                      {getStatusBadge(selectedUser)}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                  <button
                    onClick={() => setShowResetPasswordModal(selectedUser.id)}
                    className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2"
                  >
                    <Key className="w-3.5 h-3.5" /> Reset Password
                  </button>
                  {selectedUser.source !== 'client-dashboard' ? (
                    <button
                      onClick={() => setShowRoleChangeModal(selectedUser.id)}
                      className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2"
                    >
                      <UserCog className="w-3.5 h-3.5" /> Change Role
                    </button>
                  ) : (
                    isGodModeAdmin ? (
                      <button
                        onClick={() => openGodCreditModal(selectedUser.id)}
                        className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2 text-emerald-300 hover:text-emerald-200"
                      >
                        <HandCoins className="w-3.5 h-3.5" /> God Credit
                      </button>
                    ) : (
                      <div />
                    )
                  )}
                  {selectedUser.suspended ? (
                    <button
                      onClick={() => handleUnsuspendUser(selectedUser.id, selectedUser.email)}
                      disabled={suspending === selectedUser.id}
                      className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2 text-green-400 hover:text-green-300"
                    >
                      <Unlock className="w-3.5 h-3.5" /> Unsuspend
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSuspendUser(selectedUser.id, selectedUser.email)}
                      disabled={suspending === selectedUser.id}
                      className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2 text-yellow-400 hover:text-yellow-300"
                    >
                      <Ban className="w-3.5 h-3.5" /> Suspend
                    </button>
                  )}
                  <button
                    onClick={() => setShowDeleteModal(selectedUser.id)}
                    className="btn-secondary text-xs flex items-center justify-center gap-1.5 !py-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>

                {/* Detail Sections */}
                <div className="space-y-4">
                  {/* Identity */}
                  <DetailSection title="Identity" icon={User}>
                    <DetailRow label="User ID" value={selectedUser.id} copyable />
                    <DetailRow label="Email" value={selectedUser.email} copyable />
                    <DetailRow label="Display Name" value={selectedUser.displayName || 'Not set'} muted={!selectedUser.displayName} />
                    <DetailRow label="Role" value={selectedUser.role === 'admin' ? 'Administrator' : selectedUser.source === 'client-dashboard' ? 'Client' : 'Staff'} />
                    {selectedUser.source === 'client-dashboard' && (
                      <DetailRow label="Source" value="Client Dashboard" />
                    )}
                  </DetailSection>

                  {/* Subscription (client users) */}
                  {selectedUser.source === 'client-dashboard' && selectedUser.subscription && (
                    <DetailSection title="Subscription" icon={Building2}>
                      <DetailRow label="Plan" value={selectedUser.subscription.plan} />
                      <DetailRow label="Status" value={selectedUser.subscription.status} />
                    </DetailSection>
                  )}

                  {/* Organization */}
                  {selectedUser.source !== 'client-dashboard' && (
                    <DetailSection title="Organization" icon={Building2}>
                      <DetailRow label="Company" value={selectedUser.company || 'Not set'} muted={!selectedUser.company} />
                      <DetailRow label="Job Title" value={selectedUser.jobTitle || 'Not set'} muted={!selectedUser.jobTitle} />
                      <DetailRow label="Phone" value={selectedUser.phone || 'Not set'} muted={!selectedUser.phone} />
                    </DetailSection>
                  )}

                  {/* Security */}
                  <DetailSection title="Security" icon={ShieldCheck}>
                    <DetailRow
                      label="Two-Factor Auth"
                      value={selectedUser.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      valueClassName={selectedUser.twoFactorEnabled ? 'text-green-400' : 'text-yellow-400'}
                    />
                    <DetailRow
                      label="Email Verified"
                      value={selectedUser.emailVerified ? 'Verified' : 'Not Verified'}
                      valueClassName={selectedUser.emailVerified ? 'text-green-400' : 'text-yellow-400'}
                    />
                    <DetailRow
                      label="Account Activated"
                      value={selectedUser.isActivated ? 'Activated' : 'Pending'}
                      valueClassName={selectedUser.isActivated ? 'text-green-400' : 'text-yellow-400'}
                    />
                    <DetailRow
                      label="Account Status"
                      value={selectedUser.suspended ? 'Suspended' : 'Active'}
                      valueClassName={selectedUser.suspended ? 'text-red-400' : 'text-green-400'}
                    />
                    {selectedUser.suspendedAt && (
                      <DetailRow label="Suspended At" value={formatDate(selectedUser.suspendedAt)} />
                    )}
                  </DetailSection>

                  {/* Preferences (internal users only) */}
                  {selectedUser.source !== 'client-dashboard' && (
                    <DetailSection title="Preferences" icon={Palette}>
                      <DetailRow label="Theme" value={selectedUser.theme} />
                      <DetailRow label="Compact Mode" value={selectedUser.compactMode ? 'On' : 'Off'} />
                      <DetailRow label="Timezone" value={selectedUser.timezone} />
                      <DetailRow label="Date Format" value={selectedUser.dateFormat} />
                    </DetailSection>
                  )}

                  {/* Timestamps */}
                  <DetailSection title="Timestamps" icon={CalendarDays}>
                    <DetailRow label="Created" value={formatDate(selectedUser.createdAt)} />
                    <DetailRow label="Last Updated" value={formatDate(selectedUser.updatedAt)} />
                    {selectedUser.lastLoginAt && (
                      <DetailRow label="Last Login" value={formatDate(selectedUser.lastLoginAt)} />
                    )}
                  </DetailSection>

                  {/* Bio */}
                  {selectedUser.bio && (
                    <DetailSection title="Bio" icon={FileText}>
                      <p className="text-sm text-surface-300 leading-relaxed">{selectedUser.bio}</p>
                    </DetailSection>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══════════════ MODALS ═══════════════ */}

      {/* Create Admin Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader
                icon={UserPlus}
                iconBg="bg-gpu-500/20"
                iconColor="text-gpu-400"
                title="Create Admin Account"
                onClose={() => setShowCreateModal(false)}
                disabled={creating}
              />
              <div className="space-y-4">
                <div>
                  <label className="label flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Address
                  </label>
                  <input type="email" className="input" placeholder="admin@tyaus.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={creating} autoComplete="email" />
                </div>
                <div>
                  <label className="label flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Password
                  </label>
                  <input type="password" className="input" placeholder="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} disabled={creating} autoComplete="new-password" />
                </div>
                <div>
                  <label className="label flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Confirm Password
                  </label>
                  <input type="password" className="input" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={creating} autoComplete="new-password" />
                </div>
              </div>
              <ModalActions>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEmail('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  className="btn-secondary flex-1"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button onClick={handleCreateAdmin} className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={creating}>
                  {creating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" /> Create Admin</>
                  )}
                </button>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Delete User Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader
                icon={Trash2}
                iconBg="bg-red-500/20"
                iconColor="text-red-400"
                title="Delete User"
                onClose={() => setShowDeleteModal(null)}
                disabled={deleting}
              />
              <p className="text-surface-300 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-surface-100">{getUserEmail(showDeleteModal)}</span>?
              </p>
              <p className="text-sm text-red-400/80 mb-4">This action is permanent and cannot be undone. All associated data will be removed.</p>
              <ModalActions>
                <button onClick={() => setShowDeleteModal(null)} className="btn-secondary flex-1" disabled={deleting}>
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteModal, getUserEmail(showDeleteModal))}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors font-medium text-sm"
                  disabled={deleting}
                >
                  {deleting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                  ) : (
                    <><Trash2 className="w-4 h-4" /> Delete User</>
                  )}
                </button>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {showResetPasswordModal && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader
                icon={Key}
                iconBg="bg-blue-500/20"
                iconColor="text-blue-400"
                title="Reset Password"
                onClose={() => {
                  setShowResetPasswordModal(null);
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                disabled={resettingPassword}
              />
              <p className="text-surface-300 mb-4">
                Reset password for <span className="font-semibold text-surface-100">{getUserEmail(showResetPasswordModal)}</span>
              </p>
              <div className="space-y-4">
                <div>
                  <label className="label flex items-center gap-2">
                    <Lock className="w-4 h-4" /> New Password
                  </label>
                  <input type="password" className="input" placeholder="Minimum 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={resettingPassword} autoComplete="new-password" />
                </div>
                <div>
                  <label className="label flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Confirm Password
                  </label>
                  <input type="password" className="input" placeholder="Confirm password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} disabled={resettingPassword} autoComplete="new-password" />
                </div>
              </div>
              <ModalActions>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(null);
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  className="btn-secondary flex-1"
                  disabled={resettingPassword}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResetPassword(showResetPasswordModal)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={resettingPassword}
                >
                  {resettingPassword ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Resetting...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" /> Reset Password</>
                  )}
                </button>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Change Role Modal */}
      <AnimatePresence>
        {showRoleChangeModal && (() => {
          const targetUser = allUsers.find((u) => u.id === showRoleChangeModal);
          if (!targetUser || targetUser.source === 'client-dashboard') return null;
          const newRole: 'admin' | 'user' = targetUser.role === 'admin' ? 'user' : 'admin';
          return (
            <ModalOverlay>
              <ModalCard>
                <ModalHeader
                  icon={UserCog}
                  iconBg="bg-violet-500/20"
                  iconColor="text-violet-400"
                  title="Change User Role"
                  onClose={() => setShowRoleChangeModal(null)}
                  disabled={changingRole}
                />
                <p className="text-surface-300 mb-2">
                  Change role for <span className="font-semibold text-surface-100">{targetUser.email}</span>
                </p>
                <div className="flex items-center gap-3 my-4 p-3 rounded-lg bg-surface-800/50 border border-surface-700/50">
                  <div className="flex-1 text-center">
                    {getRoleBadge(targetUser.role, targetUser.source)}
                    <p className="text-xs text-surface-500 mt-1">Current</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-surface-500" />
                  <div className="flex-1 text-center">
                    {getRoleBadge(newRole)}
                    <p className="text-xs text-surface-500 mt-1">New Role</p>
                  </div>
                </div>
                {targetUser.role === 'admin' && newRole === 'user' && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mb-4">
                    <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-400">
                      Demoting this admin to staff will revoke their admin privileges. They will lose access to admin-only features.
                    </p>
                  </div>
                )}
                <ModalActions>
                  <button onClick={() => setShowRoleChangeModal(null)} className="btn-secondary flex-1" disabled={changingRole}>
                    Cancel
                  </button>
                  <button
                    onClick={() => handleChangeRole(showRoleChangeModal, newRole)}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    disabled={changingRole}
                  >
                    {changingRole ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Changing...</>
                    ) : (
                      <><CheckCircle className="w-4 h-4" /> Confirm Change</>
                    )}
                  </button>
                </ModalActions>
              </ModalCard>
            </ModalOverlay>
          );
        })()}
      </AnimatePresence>

      {/* God Credit Modal */}
      <AnimatePresence>
        {showGodCreditModal && (() => {
          const targetUser = allUsers.find((u) => u.id === showGodCreditModal);
          if (!targetUser || targetUser.source !== 'client-dashboard' || !isGodModeAdmin) return null;
          return (
            <ModalOverlay>
              <ModalCard>
                <ModalHeader
                  icon={HandCoins}
                  iconBg="bg-emerald-500/20"
                  iconColor="text-emerald-300"
                  title="God Mode Credit"
                  onClose={closeGodCreditModal}
                  disabled={submittingGodCredit}
                />
                <p className="text-surface-300 mb-4">
                  Add prepaid balance to <span className="font-semibold text-surface-100">{targetUser.email}</span> without payment processing.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="label">Amount (USD)</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="input"
                      placeholder="e.g. 100.00"
                      value={godAmount}
                      onChange={(e) => setGodAmount(e.target.value)}
                      disabled={submittingGodCredit}
                    />
                  </div>
                  <div>
                    <label className="label">Note (optional)</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Reason for manual credit"
                      value={godNote}
                      onChange={(e) => setGodNote(e.target.value)}
                      disabled={submittingGodCredit}
                      maxLength={200}
                    />
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs text-emerald-200">
                  This action is restricted to {GOD_MODE_ADMIN_EMAIL} and is audit logged.
                </div>
                <ModalActions>
                  <button onClick={closeGodCreditModal} className="btn-secondary flex-1" disabled={submittingGodCredit}>
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitGodCredit}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    disabled={submittingGodCredit}
                  >
                    {submittingGodCredit ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Crediting...</>
                    ) : (
                      <><HandCoins className="w-4 h-4" /> Credit Balance</>
                    )}
                  </button>
                </ModalActions>
              </ModalCard>
            </ModalOverlay>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}

// ──────────────────────────────────────────────
// Reusable sub-components
// ──────────────────────────────────────────────

function ModalOverlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {children}
    </div>
  );
}

function ModalCard({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="card max-w-md w-full"
    >
      {children}
    </motion.div>
  );
}

function ModalHeader({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  onClose,
  disabled,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  title: string;
  onClose: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', iconBg)}>
          <Icon className={clsx('w-5 h-5', iconColor)} />
        </div>
        <h2 className="text-xl font-semibold text-surface-100">{title}</h2>
      </div>
      <button
        onClick={onClose}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-surface-400 hover:bg-surface-800 hover:text-surface-200 transition-colors"
        disabled={disabled}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ModalActions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-3 mt-6 pt-6 border-t border-surface-800">{children}</div>;
}

function DetailSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-surface-800/50 pt-4">
      <h4 className="flex items-center gap-2 text-xs font-semibold text-surface-500 uppercase tracking-wider mb-3">
        <Icon className="w-3.5 h-3.5" /> {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  copyable,
  muted,
  valueClassName,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  muted?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-surface-500">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className={clsx('truncate max-w-[200px]', valueClassName || (muted ? 'text-surface-600 italic' : 'text-surface-200'))}>
          {value}
        </span>
        {copyable && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(value);
              toast.success('Copied');
            }}
            className="text-surface-600 hover:text-surface-300 transition-colors"
            title="Copy"
          >
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
